/**
 * sl-yphr.13: UStVA Kennzahlen Constants
 *
 * Shared between Sidebar Panel and Fullscreen Tab.
 * DRY: Single source of truth for Kz codes and grouping.
 */

/** Single UStVA Kennzahl definition */
export interface KzDefinition {
  /** Kennzahl number (e.g. 81) */
  kz: number;
  /** Human-readable name */
  label: string;
  /** Category for grouping */
  category: KzCategory;
}

export type KzCategory =
  | 'steuerpflichtig'
  | 'steuerfrei-mit'
  | 'steuerfrei-ohne'
  | 'ige'
  | 'reverse-charge'
  | 'sonstiges'
  | 'vorsteuer'
  | 'sondervorauszahlung';

export const KZ_CATEGORY_LABELS: Record<KzCategory, string> = {
  steuerpflichtig: 'Umsätze steuerpflichtig',
  'steuerfrei-mit': 'Steuerfrei mit Vorsteuerabzug',
  'steuerfrei-ohne': 'Steuerfrei ohne Vorsteuerabzug',
  ige: 'Innergemeinschaftliche Erwerbe',
  'reverse-charge': '§13b Reverse Charge',
  sonstiges: 'Sonstiges',
  vorsteuer: 'Vorsteuer',
  sondervorauszahlung: 'Sondervorauszahlung',
};

/** All 25 UStVA Kennzahlen grouped by category */
export const USTVA_KENNZAHLEN: KzDefinition[] = [
  // Umsätze steuerpflichtig
  { kz: 81, label: 'Umsätze 19%', category: 'steuerpflichtig' },
  { kz: 86, label: 'Umsätze 7%', category: 'steuerpflichtig' },
  { kz: 35, label: 'Umsätze andere Sätze', category: 'steuerpflichtig' },
  { kz: 36, label: 'Steuer andere Sätze', category: 'steuerpflichtig' },
  // Steuerfrei mit Vorsteuerabzug
  { kz: 77, label: 'EU-Lieferungen §4/1b', category: 'steuerfrei-mit' },
  { kz: 76, label: 'Ausfuhrlieferungen §4/1a', category: 'steuerfrei-mit' },
  // Steuerfrei ohne Vorsteuerabzug
  { kz: 48, label: 'Steuerfrei ohne Vorsteuer', category: 'steuerfrei-ohne' },
  // Innergemeinschaftliche Erwerbe
  { kz: 41, label: 'IGE allgemein', category: 'ige' },
  { kz: 43, label: 'IGE Steuer 19%', category: 'ige' },
  { kz: 44, label: 'IGE Steuer 7%', category: 'ige' },
  { kz: 46, label: 'IGE Fahrzeuge', category: 'ige' },
  { kz: 47, label: 'IGE Fahrzeuge Steuer', category: 'ige' },
  // §13b Reverse Charge
  { kz: 89, label: '§13b Leistungen', category: 'reverse-charge' },
  { kz: 93, label: '§13b Steuer', category: 'reverse-charge' },
  // Sonstiges
  { kz: 42, label: 'Nicht steuerbar', category: 'sonstiges' },
  { kz: 60, label: 'Land-/Forstwirtschaft', category: 'sonstiges' },
  { kz: 21, label: 'Steuer Land-/Forstw.', category: 'sonstiges' },
  // Vorsteuer
  { kz: 66, label: 'Vorsteuer Rechnungen', category: 'vorsteuer' },
  { kz: 61, label: 'Vorsteuer IGE', category: 'vorsteuer' },
  { kz: 62, label: 'Einfuhrumsatzsteuer', category: 'vorsteuer' },
  { kz: 67, label: 'Vorsteuer §13b', category: 'vorsteuer' },
  { kz: 63, label: 'Korrektur §15a Erhöhung', category: 'vorsteuer' },
  { kz: 64, label: 'Korrektur §15a Minderung', category: 'vorsteuer' },
  { kz: 59, label: 'Pauschalierung §23', category: 'vorsteuer' },
  // Sondervorauszahlung
  {
    kz: 39,
    label: 'Sondervorauszahlung 1/11',
    category: 'sondervorauszahlung',
  },
];

/** Category display order */
export const KZ_CATEGORY_ORDER: KzCategory[] = [
  'steuerpflichtig',
  'steuerfrei-mit',
  'steuerfrei-ohne',
  'ige',
  'reverse-charge',
  'sonstiges',
  'vorsteuer',
  'sondervorauszahlung',
];

/**
 * Extract Kz code from column name.
 * Convention: "Sprechender Name (KzXX)" → XX
 */
const KZ_REGEX = /\(Kz(\d+)\)/;

export function extractKzFromColumnName(name: string): number | null {
  const match = KZ_REGEX.exec(name);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Group Kz values by category for display.
 */
export function groupKzByCategory(
  values: Map<number, number>
): Map<KzCategory, { kz: number; label: string; value: number }[]> {
  const grouped = new Map<
    KzCategory,
    { kz: number; label: string; value: number }[]
  >();

  for (const category of KZ_CATEGORY_ORDER) {
    grouped.set(category, []);
  }

  for (const def of USTVA_KENNZAHLEN) {
    const value = values.get(def.kz) ?? 0;
    const group = grouped.get(def.category);
    if (group) {
      group.push({ kz: def.kz, label: def.label, value });
    }
  }

  return grouped;
}

/**
 * Calculate Zahllast (tax liability) from Kz values.
 *
 * Zahllast = USt auf Umsätze + USt auf IGE + USt §13b - Vorsteuer - SVZ
 *
 * Positive = Zahlung an FA, Negative = Erstattung
 */
export function calculateZahllast(values: Map<number, number>): number {
  const get = (kz: number) => values.get(kz) ?? 0;

  // USt auf steuerpflichtige Umsätze
  const ust19 = Math.round(get(81) * 0.19 * 100) / 100;
  const ust7 = Math.round(get(86) * 0.07 * 100) / 100;
  const ustAndere = get(36); // Kz36 = Steuer (direkt)

  // USt auf IGE
  const ustIge19 = get(43);
  const ustIge7 = get(44);
  const ustIgeFzg = get(47);

  // USt §13b
  const ust13b = get(93);

  // USt L&F
  const ustLf = get(21);

  // Vorsteuer
  const vst =
    get(66) + get(61) + get(62) + get(67) + get(63) - get(64) + get(59);

  // SVZ
  const svz = get(39);

  return (
    ust19 +
    ust7 +
    ustAndere +
    ustIge19 +
    ustIge7 +
    ustIgeFzg +
    ust13b +
    ustLf -
    vst -
    svz
  );
}
